import '@material/web/checkbox/checkbox.js'
import { html, LitElement, nothing, type PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { when } from 'lit/directives/when.js'
import { componentText, HostAriaLabelMixin, SchmancyFormField } from '@schmancy/mixins/index'

// md-checkbox exposes its internal <input type="checkbox"> only inside its
// own shadow root, so a native <label> wrapping it cannot forward clicks
// across that shadow boundary — the label's implicit activation skips it.
// We intercept label-area clicks ourselves (see the render @click handler).
//
// That inner <input> is also the one and only checkbox the accessibility tree
// can hold: it is focusable, and Chromium refuses to drop a focusable node for
// `aria-hidden` (measured — the node stays `ignored=false` with aria-hidden and
// tabindex="-1" both set on md-checkbox). So the host cannot declare a second
// `role="checkbox"` of its own without publishing a duplicate, and every
// accessible name this component is given — `aria-label`, `label`, slotted
// text — is forwarded onto md-checkbox, whose `mixinDelegatesAria` lands it on
// that inner <input>.
//
// That forwarding carries exactly `aria-label` and `aria-invalid` — the two
// md-checkbox's own render reads back off itself — so nothing this component is
// given about being READ-ONLY can reach the node holding the role. `readonly`
// therefore takes the role away from it: md-checkbox goes `inert`, which does
// drop its inner <input> from the tree where `aria-hidden` would not, and the
// span around it becomes the control — a span and not the <label> already there,
// because `<label>` admits no role but its own. One checkbox in the tree
// either way, and the readonly one is the one that can speak (measured in
// Chromium: role=checkbox, named, checked, disabled=true, focusable=true).
//
// What it says is `aria-disabled`, not `aria-readonly`. WAI-ARIA 1.2 lists
// `aria-readonly` under the checkbox role's supported properties and Chromium
// maps it to nothing there — measured against a native `<input readonly>`
// control, which reports `readonly: true` from the same probe. `aria-disabled`
// is the one signal that arrives, and it arrives without costing the tab stop,
// which is the whole distinction from `disabled`. Both are written: the second
// is what a conforming engine would read.

export type schmancyCheckBoxChangeEvent = CustomEvent<{
	value: boolean
}>

/**
 * @element schmancy-checkbox
 * @slot - The label for the checkbox.
 * @fires change - Event fired when the checkbox value changes.
 */
@customElement('schmancy-checkbox')
class SchmancyCheckboxElement extends HostAriaLabelMixin(SchmancyFormField()) {
	// Inner <md-checkbox> is the focusable surface; route host focus to it.
	protected static shadowRootOptions = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true,
	}

	// FACE wiring (formAssociated, internals, attachInternals), `name`,
	// `disabled`, `required`, `id`, `label`, `error`, `validationMessage`,
	// `validateOn`, touched/dirty/submitted, markTouched/markSubmitted,
	// FIELD_CONNECT_EVENT dispatch — all from the mixin.

	/**
	 * Boolean checked state. Test contract: `cb.value = true` flips the state.
	 * The FormData *string* is read from `true-value` attribute (or `'on'`
	 * default) — kept separate from `value` to keep the boolean-state ergonomic.
	 */
	@property({ type: Boolean, reflect: true })
	override value: boolean = false

	/** Alias for `value` for read-side ergonomics. */
	@property({ type: Boolean })
	get checked(): boolean {
		return this.value
	}
	set checked(val: boolean) {
		this.value = val
	}

	/**
	 * Who owns `value`. Unset, the box owns it: activation ticks itself and
	 * then says so. Set, the CONSUMER owns it: activation writes nothing here
	 * and only dispatches `change` carrying the state the user asked for, and
	 * the bound value is the one thing that ticks the box.
	 *
	 * Reach for it whenever the bound value is DERIVED rather than stored. A
	 * derivation is free to recompute to the value it already had, and an
	 * unchanged binding is one lit-html skips committing — so a self-ticked box
	 * keeps a state its binding never agreed to, and from then on every gesture
	 * reads back as the value the consumer is already showing. Same contract
	 * and same failure as `<schmancy-switch controlled>`.
	 */
	@property({ type: Boolean, reflect: true })
	controlled: boolean = false

	/**
	 * The third state of a tri-state box: neither on nor off, for a parent
	 * whose children are only partly selected. It is a display state, not a
	 * second value — the platform submits on `checked` alone, and activating a
	 * mixed box resolves it to checked, exactly as a native
	 * `input.indeterminate` does.
	 */
	@property({ type: Boolean, reflect: true })
	indeterminate: boolean = false

	/** The tri-state the field mounted in, so a form reset returns to it. */
	#defaultIndeterminate = false

	/** Text content of the default slot — the name when no `aria-label`/`label` is given. */
	@state()
	private slotText = ''

	override firstUpdated(changed: PropertyValues): void {
		super.firstUpdated(changed)
		this.#defaultIndeterminate = this.indeterminate
	}

	override willUpdate(changed: PropertyValues): void {
		super.willUpdate(changed)
		if (changed.has('value') || changed.has('name')) {
			this.internals?.setFormValue(this.value ? (this.getAttribute('true-value') ?? 'on') : null)
			if (this.value) this.internals?.states.add('checked')
			else this.internals?.states.delete('checked')
			this.checkValidity()
		}
		if (changed.has('indeterminate')) {
			if (this.indeterminate) this.internals?.states.add('indeterminate')
			else this.internals?.states.delete('indeterminate')
		}
		if (changed.has('required') || changed.has('disabled')) {
			this.checkValidity()
		}
	}

	override resetForm(): void {
		this.indeterminate = this.#defaultIndeterminate
		super.resetForm()
	}

	override checkValidity(): boolean {
		if (this.required && this.value !== true) {
			return this.setFieldValidity(
				{ valueMissing: true },
				this.errorMessages?.valueMissing ?? componentText('checkbox.error.valueMissing'),
			)
		}
		return this.setFieldValidity({})
	}

	override toFormEntries(): Array<[string, FormDataEntryValue]> {
		if (!this.name || this.disabled || !this.value) return []
		return [[this.name, this.getAttribute('true-value') ?? 'on']]
	}

	render() {
		// The one name, given to md-checkbox while it holds the role and to the
		// span that takes the role off it while readonly.
		const name = this.ariaLabel || this.label || this.slotText
		return html`
			<label
				class="grid grid-flow-col items-center gap-2 w-fit"
				@click=${(e: MouseEvent) => {
					// md-checkbox handles its own click internally. If the click
					// originates on or inside md-checkbox, let it propagate so
					// md-checkbox's own toggle + @change fires — intercepting it here
					// would double-toggle. For clicks on the label text / slot area
					// (outside md-checkbox's composed path) we toggle programmatically
					// and fire the change ourselves, because a native <label> cannot
					// forward a click across a shadow boundary to md-checkbox's internal
					// <input>.
					if (this.disabled) return
					if (this.readonly) {
						// `inert` already stops a click landing on md-checkbox itself;
						// this is the label-text half, and it cancels rather than merely
						// returning because a click's default action runs after
						// propagation, which is what a <label> forwards to its control.
						e.preventDefault()
						return
					}
					const path = e.composedPath()
					const mdCheckbox = this.renderRoot?.querySelector('md-checkbox')
					if (mdCheckbox && path.some(n => n === mdCheckbox || (n instanceof ShadowRoot && n.host === mdCheckbox)))
						return
					e.preventDefault()
					// Activating a mixed box resolves it to checked rather than
					// toggling off — the same move a native input.indeterminate makes.
					const requested = this.indeterminate ? true : !this.value
					if (!this.controlled) {
						this.value = requested
						this.indeterminate = false
					}
					this.markTouched()
					this.emitChange({ value: requested })
				}}
			>
				<span
					class=${this.classMap({
						contents: !this.readonly,
						'grid place-items-center rounded-lg outline-hidden focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary-default':
							this.readonly,
					})}
					role=${ifDefined(this.readonly ? 'checkbox' : undefined)}
					tabindex=${ifDefined(this.readonly && !this.disabled ? 0 : undefined)}
					aria-label=${ifDefined(this.readonly && name ? name : undefined)}
					aria-checked=${ifDefined(
						this.readonly ? (this.indeterminate ? 'mixed' : this.value === true ? 'true' : 'false') : undefined,
					)}
					aria-readonly=${ifDefined(this.readonly ? 'true' : undefined)}
					aria-disabled=${ifDefined(this.readonly ? 'true' : undefined)}
				>
					<md-checkbox
						.inert=${this.readonly}
						class=${this.classMap({
							// `outline-solid`, never a bare `outline` — the bare utility emits no
							// outline-style in the baked shadow-DOM sheet, so width and color
							// resolve while the ring stays invisible.
							'outline-solid outline-2 outline-error-default rounded-lg': this.error,
						})}
						aria-label=${name || nothing}
						.required=${this.required}
						?disabled=${this.disabled}
						.indeterminate=${this.indeterminate}
						?checked=${this.value === true}
						@change=${(e: Event) => {
							const control = e.target as HTMLElement & { checked: boolean; indeterminate: boolean }
							const requested = control.checked
							// md-checkbox ticks itself before it tells us, and the `?checked=`
							// / `.indeterminate=` bindings below re-commit only when the value
							// they read changes — so every path that does NOT accept
							// `requested` has to put the inner control back by hand, or the
							// box and the value this host holds stay out of step for good.
							if (this.controlled || this.readonly || this.disabled) {
								control.checked = this.value === true
								control.indeterminate = this.indeterminate
							} else {
								this.value = requested
								this.indeterminate = control.indeterminate
							}
							if (this.readonly || this.disabled) return
							this.markTouched()
							this.emitChange({ value: requested })
						}}
					>
					</md-checkbox>
				</span>
				${when(
					this.label,
					() => html`<span aria-hidden="true" class=${this.error ? 'text-error-default' : ''}>${this.label}</span>`,
					() =>
						html`<slot
							@slotchange=${(e: Event) => {
							this.slotText = (e.target as HTMLSlotElement)
								.assignedNodes({ flatten: true })
								.map(n => n.textContent ?? '')
								.join(' ')
								.trim()
						}}
						></slot>`,
				)}
			</label>
			${
				this.error && this.validationMessage
					? html`<div role="alert" class="text-error-default text-xs mt-1">${this.validationMessage}</div>`
					: nothing
			}
		`
	}
}

export { SchmancyCheckboxElement as SchmancyCheckbox }

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-checkbox': SchmancyCheckboxElement
	}
}
